// FeaturedVideoAdmin — CMS mínimo do Vídeo em Destaque do YouTube (Home P0.1).
//
// Regras editoriais:
//  - Aceita URL completa OU video_id (11 chars).
//  - Apenas UM vídeo com status=published ativo por vez.
//  - Ao publicar novo, o backend automaticamente move os antigos p/ hidden.
//  - Vídeos fora da janela [starts_at, ends_at] são ignorados.
//  - Exclusão exige confirmação explícita no UI.
//
// Endpoints:
//   GET    /api/admin/featured-video
//   POST   /api/admin/featured-video
//   PATCH  /api/admin/featured-video/{id}
//   DELETE /api/admin/featured-video/{id}
//   GET    /api/featured-video/current   (público — para pré-visualização)

import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Youtube, Trash2, Eye, EyeOff, Save, X, Plus, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_LABELS = {
  draft: "Rascunho",
  published: "Publicado",
  hidden: "Oculto",
};

// -----------------------------------------------------------------------------
// P0.4A — Editorial status computed client-side.
// Objetivo: eliminar strings técnicas (published/draft/hidden/video_id) do
// fluxo principal e substituir por situações editoriais compreensíveis.
// Regras (determinísticas):
//   Inválido                       → falta video_id, título ou datas de override
//   Substituição especial ativa    → is_manual_override + today ∈ [start, end]
//   Substituição especial agendada → is_manual_override + today < start
//   Encerrado                      → today > end (override) OU scheduled_date < today
//   Ativo hoje                     → id == activeId (retornado pelo resolver público)
//   Agendado                       → devotional-linked com data futura
//   Relacionado ao devocional      → tem devotional_id mas não ativo hoje
//   Vídeo de reserva               → is_evergreen (não é o ativo hoje)
// -----------------------------------------------------------------------------
const RECIFE_OFFSET_HOURS = -3;

function todayRecifeISO() {
  const now = new Date();
  const shifted = new Date(now.getTime() + RECIFE_OFFSET_HOURS * 3600 * 1000);
  return shifted.toISOString().slice(0, 10);
}

function computeEditorialStatus(item, activeId, today) {
  // Sanity checks — Inválido
  const missingVideo = !item.video_id || String(item.video_id).length !== 11;
  const missingTitle = !item.title || !String(item.title).trim();
  const overrideNoWindow = item.is_manual_override && (!item.override_start_date || !item.override_end_date);
  if (missingVideo || missingTitle || overrideNoWindow) {
    const reasons = [];
    if (missingVideo) reasons.push("ID de YouTube ausente ou inválido");
    if (missingTitle) reasons.push("título não preenchido");
    if (overrideNoWindow) reasons.push("período da substituição especial incompleto");
    return { key: "invalido", label: "Inválido", tone: "rose", reason: reasons.join(" · ") };
  }

  const isActive = activeId && String(item.id) === String(activeId);
  if (isActive) {
    // Diferencia origem quando ativo
    if (item.is_manual_override) {
      return { key: "override_active", label: "Substituição especial ativa", tone: "purple" };
    }
    if (item.is_evergreen) {
      return { key: "evergreen_active", label: "Vídeo de reserva (em exibição)", tone: "emerald" };
    }
    return { key: "active_today", label: "Ativo hoje", tone: "emerald" };
  }

  // Substituição especial
  if (item.is_manual_override) {
    const start = item.override_start_date;
    const end = item.override_end_date;
    if (end && today > end) {
      return { key: "encerrado_override", label: "Encerrado", tone: "slate" };
    }
    if (start && today < start) {
      return { key: "override_scheduled", label: "Substituição especial agendada", tone: "amber" };
    }
    // Dentro da janela mas não é o ativo (pode acontecer quando há vários)
    return { key: "override_pending", label: "Substituição especial pendente", tone: "amber" };
  }

  // Devocional-linked
  if (item.devotional_id) {
    if (item.scheduled_date) {
      if (item.scheduled_date > today) {
        return { key: "agendado", label: "Agendado", tone: "sky" };
      }
      if (item.scheduled_date < today) {
        return { key: "encerrado_devo", label: "Encerrado", tone: "slate" };
      }
    }
    return { key: "devo_linked", label: "Relacionado ao devocional", tone: "sky" };
  }

  // Evergreen (mas não ativo hoje)
  if (item.is_evergreen) {
    return { key: "reserva", label: "Vídeo de reserva", tone: "gold" };
  }

  // Fallback — não vinculado a nada, sem override, sem evergreen
  return { key: "sem_vinculo", label: "Sem vínculo editorial", tone: "slate" };
}

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  purple: "bg-purple-100 text-purple-800 border border-purple-200",
  amber: "bg-amber-100 text-amber-800 border border-amber-200",
  sky: "bg-sky-100 text-sky-800 border border-sky-200",
  gold: "bg-gold/15 text-gold border border-gold/40",
  slate: "bg-slate-100 text-slate-700 border border-slate-200",
  rose: "bg-rose-100 text-rose-800 border border-rose-200",
};

const EMPTY_FORM = {
  url_or_id: "",
  title: "",
  description: "",
  starts_at: "",
  ends_at: "",
  status: "published",
  // P0.4A — automação editorial
  devotional_id: "",
  scheduled_date: "",
  base_reference: "",
  is_evergreen: false,
  evergreen_priority: 0,
  is_manual_override: false,
  override_start_date: "",
  override_end_date: "",
  override_reason: "",
};

function extractIdPreview(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  const idRe = /^[A-Za-z0-9_-]{11}$/;
  if (idRe.test(trimmed)) return trimmed;
  const patterns = [
    /youtube\.com\/watch\?v=([A-Za-z0-9_-]{11})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = trimmed.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function FeaturedVideoAdmin() {
  const [items, setItems] = useState([]);
  const [devotionals, setDevotionals] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeSource, setActiveSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [duplicateConflict, setDuplicateConflict] = useState(null); // {existing_id, existing_title}

  const today = useMemo(() => todayRecifeISO(), []);
  const devoIndex = useMemo(() => {
    const idx = {};
    devotionals.forEach((d) => { idx[String(d.id)] = d; });
    return idx;
  }, [devotionals]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d, cur] = await Promise.all([
        api.get("/admin/featured-video"),
        api.get("/admin/devotionals-selector").catch(() => ({ data: [] })),
        api.get("/featured-video/current").catch(() => ({ data: null })),
      ]);
      setItems(Array.isArray(r.data) ? r.data : []);
      setDevotionals(Array.isArray(d.data) ? d.data : []);
      // /featured-video/current retorna o próprio doc (FeaturedVideoOut) ou null.
      setActiveId(cur.data?.id || null);
      // Origem é inferida do próprio doc: override > devotional_id > evergreen.
      if (cur.data) {
        if (cur.data.is_manual_override) setActiveSource("manual_override");
        else if (cur.data.devotional_id) setActiveSource("devotional");
        else if (cur.data.is_evergreen) setActiveSource("evergreen");
        else setActiveSource(null);
      } else {
        setActiveSource(null);
      }
    } catch (e) {
      toast.error("Não foi possível carregar os vídeos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // P0.4A: modal de conflito de unicidade (dois vídeos para o mesmo devocional).
  useEffect(() => {
    if (!duplicateConflict) return;
    const ok = window.confirm(
      `Já existe um vídeo associado a este devocional (“${duplicateConflict.existing_title || "sem título"}”). ` +
      `Deseja excluir o anterior e substituir pelo novo?`
    );
    setDuplicateConflict(null);
    if (ok && duplicateConflict.existing_id) {
      api.delete(`/admin/featured-video/${duplicateConflict.existing_id}`)
        .then(() => {
          toast.success("Vídeo anterior removido. Envie o novo novamente.");
          load();
        })
        .catch(() => toast.error("Falha ao remover o vídeo anterior."));
    }
  }, [duplicateConflict]);

  const previewId = extractIdPreview(form.url_or_id);
  const previewValid = !!previewId;

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      url_or_id: item.video_id || "",
      title: item.title || "",
      description: item.description || "",
      starts_at: item.starts_at || "",
      ends_at: item.ends_at || "",
      status: item.status || "draft",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!previewId) {
      toast.error("URL ou video_id do YouTube inválido.");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Título é obrigatório.");
      return;
    }
    // P0.4A: se marcou substituição manual, exigir confirmação explícita.
    if (form.is_manual_override && !confirmOverride) {
      const start = form.override_start_date || "hoje";
      const end = form.override_end_date || "sem data final";
      const ok = window.confirm(
        `Esta substituição ficará acima do vídeo relacionado ao devocional durante o período de ${start} a ${end}. Deseja confirmar?`
      );
      if (!ok) return;
      setConfirmOverride(true);
    }
    setSaving(true);
    try {
      const payload = {
        url_or_id: form.url_or_id.trim(),
        title: form.title.trim(),
        description: form.description.trim() || null,
        status: form.status,
        devotional_id: form.devotional_id || null,
        scheduled_date: form.scheduled_date || null,
        base_reference: form.base_reference || null,
        is_evergreen: !!form.is_evergreen,
        evergreen_priority: Number(form.evergreen_priority) || 0,
        is_manual_override: !!form.is_manual_override,
        override_start_date: form.override_start_date || null,
        override_end_date: form.override_end_date || null,
        override_reason: form.override_reason || null,
      };
      if (editingId) {
        await api.patch(`/admin/featured-video/${editingId}`, payload);
        toast.success("Vídeo atualizado.");
      } else {
        await api.post("/admin/featured-video", payload);
        toast.success("Vídeo associado com sucesso.");
      }
      cancelEdit();
      setConfirmOverride(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "duplicate_devotional_video") {
        setDuplicateConflict(detail);
      } else {
        const msg = typeof detail === "string" ? detail : "Falha ao salvar.";
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (item, newStatus) => {
    try {
      await api.patch(`/admin/featured-video/${item.id}`, { status: newStatus });
      toast.success(`Status alterado para ${STATUS_LABELS[newStatus] || newStatus}.`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao alterar status.";
      toast.error(msg);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/featured-video/${id}`);
      toast.success("Vídeo removido.");
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      toast.error("Falha ao remover.");
    }
  };

  return (
    <section className="space-y-6" data-testid="admin-featured-video">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Youtube className="w-5 h-5 text-gold" />
            <h2 className="font-display text-2xl text-navy">Vídeo em Destaque da Home</h2>
          </div>
          <p className="text-sm text-navy/65 mt-1 max-w-2xl">
            Bloco 9 da Home pública. Somente um vídeo publicado por vez.
            ao publicar um novo, o anterior é automaticamente ocultado.
            Aceita URL completa do YouTube ou o video_id direto.
          </p>
        </div>
        {editingId === null && (
          <button
            type="button"
            onClick={startCreate}
            data-testid="admin-fv-new-btn"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Novo vídeo
          </button>
        )}
      </header>

      {/* Estado atual publicado */}
      <div
        data-testid="admin-fv-current"
        className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${
          activeId ? "border-emerald-300 bg-emerald-50" : "border-navy/15 bg-[hsl(var(--parchment))]"
        }`}
      >
        {(() => {
          const activeItem = items.find((x) => String(x.id) === String(activeId));
          if (!activeItem) {
            return (
              <p className="text-sm text-navy/70">
                Nenhum vídeo em exibição no momento. A Home mostra silêncio elegante (sem placeholder).
              </p>
            );
          }
          const sourceLabel =
            activeSource === "manual_override"
              ? "Substituição especial ativa"
              : activeSource === "evergreen"
              ? "Vídeo de reserva em exibição"
              : "Ativo hoje · Devocional de hoje";
          return (
            <>
              <img
                src={activeItem.thumbnail_url}
                alt="Thumbnail"
                className="w-full sm:w-24 h-32 sm:h-14 object-cover rounded-md border border-navy/10"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-700" data-testid="admin-fv-current-label">
                  {sourceLabel}
                </p>
                <p className="font-medium text-navy truncate" data-testid="admin-fv-current-title">
                  {activeItem.title}
                </p>
                <p className="text-xs text-navy/60">Recife · {today}</p>
              </div>
              <a
                href={`https://youtube.com/watch?v=${activeItem.video_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-gold"
                data-testid="admin-fv-current-preview"
              >
                Pré-visualizar <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </>
          );
        })()}
      </div>

      {/* Formulário */}
      <form onSubmit={submit} data-testid="admin-fv-form" className="bg-white border border-border rounded-2xl p-5 sm:p-6 space-y-4">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          {editingId ? "Editar vídeo" : "Novo vídeo"}
        </p>

        <label className="block">
          <span className="text-xs text-navy/70">URL completa do YouTube ou video_id</span>
          <input
            type="text"
            value={form.url_or_id}
            onChange={(e) => setForm({ ...form, url_or_id: e.target.value })}
            data-testid="admin-fv-url-input"
            placeholder="https://youtube.com/watch?v=... ou 11 caracteres"
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
            required
          />
          {form.url_or_id && (
            <p className={`mt-1 text-xs ${previewValid ? "text-emerald-600" : "text-rose-600"}`} data-testid="admin-fv-url-feedback">
              {previewValid ? `video_id detectado: ${previewId}` : "formato inválido"}
            </p>
          )}
        </label>

        <label className="block">
          <span className="text-xs text-navy/70">Título</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="admin-fv-title-input"
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
            required
          />
        </label>

        <label className="block">
          <span className="text-xs text-navy/70">Descrição curta</span>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="admin-fv-description-input"
            className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
        </label>

        {/* Seletor de Devocional (P0.4A) — automação editorial. */}
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2">
            <span className="text-xs text-navy/70">Devocional relacionado</span>
            <select
              value={form.devotional_id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const d = devotionals.find((x) => String(x.id) === String(id));
                setForm({
                  ...form,
                  devotional_id: id,
                  scheduled_date: d?.date || form.scheduled_date,
                  base_reference: d?.base_reference || form.base_reference,
                });
              }}
              data-testid="admin-fv-devotional-select"
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold/40"
            >
              <option value="">nenhum (evergreen ou override)</option>
              {devotionals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.date ? `${d.date} · ` : ""}{d.base_reference ? `${d.base_reference} · ` : ""}{d.title}
                </option>
              ))}
            </select>
            {form.base_reference && (
              <p className="mt-1 text-xs text-navy/55" data-testid="admin-fv-base-reference">
                Versículo-base: <b>{form.base_reference}</b>
              </p>
            )}
          </label>
        </div>

        {/* Opções editoriais (P0.4A) */}
        <div className="rounded-xl border border-border bg-[hsl(var(--parchment))] p-4 space-y-3" data-testid="admin-fv-editorial-options">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.is_evergreen}
              onChange={(e) => setForm({ ...form, is_evergreen: e.target.checked, is_manual_override: e.target.checked ? false : form.is_manual_override })}
              data-testid="admin-fv-evergreen-toggle"
              className="mt-0.5"
            />
            <span>
              <b>Usar como vídeo de reserva (evergreen)</b>
              <span className="block text-xs text-navy/60">
                Atemporal. Só é usado quando não existe vídeo específico para o Devocional do Dia.
              </span>
            </span>
          </label>
          {form.is_evergreen && (
            <label className="block ml-6">
              <span className="text-xs text-navy/70">Prioridade do conteúdo evergreen</span>
              <input
                type="number"
                value={form.evergreen_priority}
                onChange={(e) => setForm({ ...form, evergreen_priority: parseInt(e.target.value, 10) || 0 })}
                data-testid="admin-fv-evergreen-priority"
                className="mt-1 w-24 border border-border rounded-lg px-3 py-1.5 text-sm bg-white"
              />
            </label>
          )}
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!form.is_manual_override}
              onChange={(e) => setForm({ ...form, is_manual_override: e.target.checked, is_evergreen: e.target.checked ? false : form.is_evergreen })}
              data-testid="admin-fv-override-toggle"
              className="mt-0.5"
            />
            <span>
              <b>Substituição especial (datas especiais)</b>
              <span className="block text-xs text-navy/60">
                Sobrepõe o vídeo automático durante o período informado (Natal, Páscoa, campanhas etc.).
              </span>
            </span>
          </label>
          {form.is_manual_override && (
            <div className="grid sm:grid-cols-3 gap-3 ml-6">
              <label className="block">
                <span className="text-xs text-navy/70">Início da substituição</span>
                <input
                  type="date"
                  value={form.override_start_date}
                  onChange={(e) => setForm({ ...form, override_start_date: e.target.value })}
                  data-testid="admin-fv-override-start"
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-navy/70">Encerramento</span>
                <input
                  type="date"
                  value={form.override_end_date}
                  onChange={(e) => setForm({ ...form, override_end_date: e.target.value })}
                  data-testid="admin-fv-override-end"
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-navy/70">Motivo / evento</span>
                <input
                  type="text"
                  value={form.override_reason}
                  onChange={(e) => setForm({ ...form, override_reason: e.target.value })}
                  data-testid="admin-fv-override-reason"
                  placeholder="Ex: Natal, Páscoa, Cursilho"
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2 min-h-[44px] text-sm"
                />
              </label>
            </div>
          )}
        </div>

        {/* Pré-visualização inline */}
        {previewValid && (
          <div className="mt-2 rounded-xl border border-border bg-[hsl(var(--parchment))] p-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55 mb-2">Pré-visualização</p>
            <div className="flex gap-3">
              <img
                src={`https://img.youtube.com/vi/${previewId}/hqdefault.jpg`}
                alt="Thumbnail preview"
                className="w-32 h-20 object-cover rounded-md border border-navy/10"
                data-testid="admin-fv-preview-thumbnail"
              />
              <div className="min-w-0 text-sm text-navy/75">
                <p className="font-medium truncate text-navy">{form.title || "(sem título)"}</p>
                <p className="text-xs mt-0.5">video_id: {previewId}</p>
                <a
                  href={`https://youtube.com/watch?v=${previewId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-navy/60 hover:text-gold mt-1"
                >
                  Abrir no YouTube <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center gap-2 pt-3 border-t border-border">
          <button
            type="submit"
            disabled={saving || !previewValid || !form.title.trim()}
            data-testid="admin-fv-save-btn"
            className="inline-flex items-center justify-center gap-2 min-h-[44px] bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar vídeo"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="admin-fv-cancel-btn"
              className="inline-flex items-center justify-center gap-2 min-h-[44px] border border-border text-navy/70 hover:text-navy px-5 py-2 rounded-full text-[11px] uppercase tracking-widest w-full sm:w-auto"
            >
              <X className="w-3.5 h-3.5" /> Cancelar
            </button>
          )}
        </div>
      </form>

      {/* Lista */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="admin-fv-list">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Todos os vídeos</p>
          <p className="text-xs text-navy/50">{items.length} {items.length === 1 ? "item" : "itens"}</p>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-navy/60">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center space-y-3" data-testid="admin-fv-empty">
            <p className="text-sm text-navy/60">Ainda não existem vídeos cadastrados.</p>
            <button
              type="button"
              onClick={startCreate}
              data-testid="admin-fv-empty-cta"
              className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Cadastrar primeiro vídeo
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((it) => {
              const st = computeEditorialStatus(it, activeId, today);
              const linkedDevo = it.devotional_id ? devoIndex[String(it.devotional_id)] : null;
              return (
              <li key={it.id} data-testid={`admin-fv-item-${it.id}`} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                <img
                  src={it.thumbnail_url}
                  alt="thumb"
                  className="w-full sm:w-24 h-40 sm:h-14 object-cover rounded-md border border-navy/10 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-navy break-words">{it.title}</p>

                  {/* Badge amigável */}
                  <div className="flex items-start flex-wrap gap-2 mt-1.5">
                    <span
                      data-testid={`admin-fv-item-status-${it.id}`}
                      data-status-key={st.key}
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${TONE_CLASSES[st.tone] || TONE_CLASSES.slate}`}
                    >
                      {st.label}
                    </span>
                    {st.reason && (
                      <span className="text-[11px] text-rose-700" data-testid={`admin-fv-item-reason-${it.id}`}>
                        Motivo: {st.reason}
                      </span>
                    )}
                  </div>

                  {/* Bloco de origem editorial */}
                  <div className="mt-2 text-xs text-navy/70 space-y-0.5" data-testid={`admin-fv-item-origin-${it.id}`}>
                    {linkedDevo && (
                      <p>
                        <span className="text-navy/50">Devocional vinculado:</span>{" "}
                        <b>{linkedDevo.date || "sem data"}</b>
                        {linkedDevo.base_reference ? ` · ${linkedDevo.base_reference}` : ""}
                        {linkedDevo.title ? ` · ${linkedDevo.title}` : ""}
                      </p>
                    )}
                    {it.is_manual_override && (
                      <p>
                        <span className="text-navy/50">Substituição especial:</span>{" "}
                        <b>{it.override_start_date || "?"}</b> → <b>{it.override_end_date || "?"}</b>
                        {it.override_reason ? ` · ${it.override_reason}` : ""}
                      </p>
                    )}
                    {it.is_evergreen && !it.is_manual_override && (
                      <p>
                        <span className="text-navy/50">Reserva evergreen:</span>{" "}
                        prioridade <b>{it.evergreen_priority ?? 0}</b>
                        {it.last_shown_date ? ` · última exibição ${it.last_shown_date}` : " · nunca exibida"}
                      </p>
                    )}
                  </div>

                  {/* Detalhes técnicos (recolhidos por padrão) */}
                  <details className="mt-2 text-xs text-navy/55" data-testid={`admin-fv-item-tech-${it.id}`}>
                    <summary className="cursor-pointer select-none hover:text-navy">Ver detalhes técnicos</summary>
                    <div className="mt-1.5 pl-2 border-l border-border space-y-0.5">
                      <p>id: <span className="font-mono">{it.id}</span></p>
                      <p>video_id: <span className="font-mono">{it.video_id}</span></p>
                      <p>status DB: <span className="font-mono">{it.status}</span></p>
                    </div>
                  </details>
                </div>
                {/* Ações — full width no mobile, ao lado no desktop */}
                <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:shrink-0 pt-2 border-t border-border sm:border-t-0 sm:pt-0">
                  {it.status !== "published" && (
                    <button
                      type="button"
                      onClick={() => changeStatus(it, "published")}
                      data-testid={`admin-fv-publish-${it.id}`}
                      title="Publicar"
                      className="min-h-[40px] min-w-[40px] rounded-full border border-emerald-300 text-emerald-600 hover:bg-emerald-50 flex items-center justify-center"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {it.status === "published" && (
                    <button
                      type="button"
                      onClick={() => changeStatus(it, "hidden")}
                      data-testid={`admin-fv-hide-${it.id}`}
                      title="Ocultar"
                      className="min-h-[40px] min-w-[40px] rounded-full border border-navy/25 text-navy/70 hover:bg-navy/5 flex items-center justify-center"
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => startEdit(it)}
                    data-testid={`admin-fv-edit-${it.id}`}
                    className="min-h-[40px] text-[11px] uppercase tracking-widest text-navy/70 hover:text-gold px-4 py-1.5 rounded-full border border-border"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(it.id)}
                    data-testid={`admin-fv-delete-${it.id}`}
                    title="Excluir"
                    className="min-h-[40px] min-w-[40px] rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 flex items-center justify-center ml-auto sm:ml-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal de confirmação de exclusão */}
      {confirmDeleteId && (
        <div
          data-testid="admin-fv-confirm-delete"
          className="fixed inset-0 bg-navy/60 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            className="bg-white rounded-2xl border border-border max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-display text-lg text-navy">Excluir vídeo?</p>
            <p className="text-sm text-navy/70 mt-2">
              Esta ação não pode ser desfeita. O registro será removido do banco de dados.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                data-testid="admin-fv-confirm-cancel"
                className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest border border-border text-navy/70"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => doDelete(confirmDeleteId)}
                data-testid="admin-fv-confirm-yes"
                className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest bg-rose-600 text-white hover:bg-rose-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
